import { Reducer } from 'redux'
import { AzureChangeType, AzureFormActions, mapAzureApiToForm } from './actions'
import {
  PartialAzureFormObject,
  ReferralAzureForm,
  ReferralAzureFormSections,
  ReferralStatus,
  ReferralSubstatus,
  AzureFormStatus,
  azureReferralFormInitialSections,
  mergeAzureReferralFormSections,
} from './types'

import {
  AzureTeamMember,
  AzureReferralSolution,
  MicrosoftReferral,
} from '../../../api/CoSell/index'

export interface ReferralForm {
  referralForm: ReferralAzureFormSections
  referral?: Partial<PartialAzureFormObject>
  formChanged: boolean
  errorsFromBackend?: Partial<Record<keyof ReferralAzureForm, string>>
  teamMembers?: AzureTeamMember[]
  /** Microsoft AEs from microsoftReferral; cleared/reset on each getAzureReferral. */
  microsoftReferral?: MicrosoftReferral
  solutions?: AzureReferralSolution[]
  status?: ReferralStatus | string
  substatus?: ReferralSubstatus | string
  formStatus?: AzureFormStatus
  engagementId?: string
  salesStage?: string
}

export const initialState: ReferralForm = {
  referralForm: azureReferralFormInitialSections,
  formChanged: false,
  teamMembers: [],
  solutions: [],
  status: undefined,
  substatus: undefined,
}

type actionType =
  | {
      type: AzureFormActions.SET_AZURE_FORM
      payload: Partial<ReferralAzureFormSections>
    }
  | {
      type: AzureFormActions.UPDATE_AZURE_FORM
      payload: AzureChangeType
    }
  | {
      type: AzureFormActions.SET_AZURE
      payload: PartialAzureFormObject
    }
  | {
      type: AzureFormActions.SET_FORM_AZURE_ERRORS
      payload: Partial<Record<keyof ReferralAzureForm, string>>
    }
  | {
      type: AzureFormActions.CLEAR_AZURE_FORM
    }
  | {
      type: AzureFormActions.SET_AZURE_TEAM_MEMBERS
      payload: AzureTeamMember[]
    }
  | {
      type: AzureFormActions.SET_AZURE_SOLUTIONS
      payload: AzureReferralSolution[]
    }
  | {
      type: AzureFormActions.SET_AZURE_STATUS
      payload: {
        status: ReferralStatus | string
        substatus: ReferralSubstatus | string
      }
    }
  | {
      type: AzureFormActions.SET_AZURE_FORM_METADATA
      payload: {
        formStatus?: AzureFormStatus
        engagementId?: string
        salesStage?: string
      }
    }
  | {
      type: AzureFormActions.SET_MICROSOFT_REFERRAL
      payload: MicrosoftReferral
    }

export const reducer: Reducer<ReferralForm, actionType> = (
  state = initialState,
  action
) => {
  switch (action.type) {
    case AzureFormActions.SET_AZURE_FORM: {
      return {
        ...state,
        referralForm: mergeAzureReferralFormSections(
          state.referralForm,
          action.payload
        ),
      }
    }
    case AzureFormActions.UPDATE_AZURE_FORM: {
      const { sectionName, data } = action.payload
      return {
        ...state,
        referralForm: {
          ...state.referralForm,
          [sectionName]: {
            ...state.referralForm[sectionName],
            ...data,
          },
        },
      }
    }
    case AzureFormActions.SET_AZURE: {
      // Convert from API format to form format
      const referralsObject = action.payload.referralObjectTransformed
        ? action.payload.referralObjectTransformed
        : mapAzureApiToForm(action.payload.referralObject || {})
      return {
        ...state,
        referral: {
          ...state.referral,
          ...action.payload,
          referralObjectTransformed: referralsObject,
        },
      }
    }
    case AzureFormActions.SET_FORM_AZURE_ERRORS: {
      return {
        ...state,
        errorsFromBackend: {
          ...action.payload,
        },
      }
    }
    case AzureFormActions.CLEAR_AZURE_FORM: {
      return (state = initialState)
    }
    case AzureFormActions.SET_AZURE_TEAM_MEMBERS: {
      return {
        ...state,
        teamMembers: action.payload,
      }
    }
    case AzureFormActions.SET_AZURE_SOLUTIONS: {
      return {
        ...state,
        solutions: action.payload,
      }
    }
    case AzureFormActions.SET_AZURE_STATUS: {
      return {
        ...state,
        status: action.payload.status,
        substatus: action.payload.substatus,
      }
    }
    case AzureFormActions.SET_AZURE_FORM_METADATA: {
      return {
        ...state,
        ...(action.payload.formStatus !== undefined
          ? { formStatus: action.payload.formStatus }
          : {}),
        ...(action.payload.engagementId !== undefined
          ? { engagementId: action.payload.engagementId }
          : {}),
        ...(action.payload.salesStage !== undefined
          ? { salesStage: action.payload.salesStage }
          : {}),
      }
    }
    case AzureFormActions.SET_MICROSOFT_REFERRAL: {
      return {
        ...state,
        microsoftReferral: action.payload,
      }
    }

    default: {
      return state
    }
  }
}
